<template>
  <WrappedForm
    ref="form"
    class="language-editor"
    labelBold
    labelWidth="150px"
    :model="languageData"
    :formLines="formLines"
  >
  </WrappedForm>
</template>

<script>
import _ from 'lodash'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import { validateThreeDigitIntegerTwoDigitDecimal } from '@/utils/validator'

export default {
  components: {
    WrappedForm
  },
  props: {
    setting: {
      type: Object,
      required: false
    },
    languageInfoList: {
      type: Array,
      default: null
    },
    levelList: {
      type: Array,
      default: null
    },
    certificationList: {
      type: Array,
      default: null
    },
    language: {
      type: Object,
      default: null
    }

  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      languageData: {}
    }
  },
  computed: {
    isCandidate () {
      if (this.setting?.isCandidate !== undefined) {
        return this.setting?.isCandidate
      }
      return false
    },
    hiddenField () {
      return this.setting?.hiddenField || []
    },
    displayField () {
      return this.setting?.displayField || []
    },
    requiredField () {
      return this.setting?.requiredField || []
    },
    disabledField () {
      return this.setting?.disabledField || []
    },
    formLines () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const requiredField = []
      if (this.requiredField) {
        requiredField.push(...this.requiredField)
      }
      const hiddenField = []
      if (this.hiddenField) {
        hiddenField.push(...this.hiddenField)
      }
      const displayField = []
      if (this.displayField) {
        displayField.push(...this.displayField)
      }
      const disabledField = []
      if (this.disabledField) {
        disabledField.push(...this.disabledField)
      }
      const lines = {
        language: {
          disabled: disabledField.includes('language'),
          hidden: hiddenField.includes('language') && !displayField.includes('language'),
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.language`),
          option: {
            data: this.languageInfoList,
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          props: { filterable: true },
          rules: [{ required: requiredField.includes('language'), message: requiredMessage, trigger: 'change' }]
        },
        level: {
          disabled: disabledField.includes('level'),
          hidden: hiddenField.includes('level') && !displayField.includes('level'),
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.level`),
          option: {
            data: this.levelList,
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          props: { filterable: true },
          rules: [{ required: requiredField.includes('level'), message: requiredMessage, trigger: 'change' }]
        },
        certification: {
          disabled: disabledField.includes('certification'),
          hidden: hiddenField.includes('certification') && !displayField.includes('certification'),
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.certification`),
          option: {
            data: this.certificationList,
            valueKey: 'id',
            labelKey: 'i18nName'
          },
          props: { filterable: true },
          rules: [{ required: requiredField.includes('certification'), message: requiredMessage, trigger: 'change' }]
        },
        score: {
          disabled: disabledField.includes('score'),
          hidden: hiddenField.includes('score') && !displayField.includes('score'),
          label: this.$t(`${this.i18nPrefix}.label.score`),
          rules: [{ required: requiredField.includes('score'), message: requiredMessage, trigger: 'change' }, { validator: validateThreeDigitIntegerTwoDigitDecimal, trigger: 'change' }]
        }
      }
      const vm = this
      _.forEach([lines.certification, lines.score], item => {
        Object.defineProperty(item, 'hidden', {
          configurable: true,
          enumerable: true,
          get () {
            const language = vm.languageData
            return !(language && (language.language === 'ENG' && language.level !== 'NAT'))
          }
        })
      })
      return lines
    }
  },
  created () {},
  methods: {
    async getFormRef () {
      if (!this.$refs.form) {
        await this.$nextTick()
      }
      return this.$refs.form
    },
    clearData () {
      this.languageData = this.toData(null)
      this.$nextTick(() => this.clearValidate())
    },
    toData (language) {
      if (!language) {
        language = {}
      }
      return {
        language: language.language,
        level: language.level,
        certification: language.certification,
        score: language.score
      }
    },
    async clearValidate () {
      const form = await this.getFormRef()
      if (form) {
        form.clearValidate()
      }
    },
    async submit () {
      const form = await this.getFormRef()
      const isValid = await form.submit()
      if (!isValid) {
        return null
      } else {
        const language = this.languageData
        return {
          language: language.language,
          level: language.level,
          certification: language.certification,
          score: language.score
        }
      }
    }
  },
  watch: {
    language: {
      handler () {
        this.languageData = this.toData(this.language)
        this.$nextTick(() => this.clearValidate())
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.language-editor {
  :v-deep {
    .el-select-dropdown {
      max-width: 48%;
    }

    .el-textarea {
      word-break: initial;
    }
  }
}
</style>
